import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, TextField, Typography, Link as MuiLink } from "@mui/material";

// INITIAL FORM DATA - Default values for creating a new analyst
export const analystInitialData = {
  id: '',
  name: '',
  email: '',
};

// FORM VALIDATION
export function validateAnalyst(data) {
  const errors = {};

  // Name is required
  if (!data.name || !data.name.trim()) {
    errors.name = 'Name is required';
  }

  // Validate email format if provided
  if (data.email && data.email.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      errors.email = 'Please enter a valid email address';
    }
  }

  return errors;
}

// NULLABLE FIELDS - Fields that should be converted from empty string to null
export const analystNullableFields = [
  'email',
];

// Simple FormField that handles both view and edit modes
const FormField = ({ label, value, editable, onChange, error, helperText, required, type, isEmail }) => {
  if (!editable) {
    // View mode
    const displayValue = value || '—';

    // Render as mailto link
    if (isEmail && displayValue !== '—') {
      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <MuiLink
            href={`mailto:${displayValue}`}
            underline="always"
            sx={{
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        </Box>
      );
    }
    
    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary' }}>
          {displayValue}
        </Typography>
      </Box>
    );
  }
  
  // Edit mode
  return (
    <TextField
      label={label}
      value={value || ''}
      fullWidth
      required={required}
      type={type}
      onChange={onChange}
      variant="outlined"
      error={!!error}
      helperText={error || helperText || ''}
      InputLabelProps={{
        sx: { color: 'primary.main', fontWeight: 600 }
      }}
    />
  );
};

const AnalystFormFields = ({ data, editable, onChange, errors = {}, displayName }) => {
  const gridSpacing = editable ? 3 : 4;
  const gridBreakpoint = editable ? 'sm' : 'md';
  
  return (
    <Box component={editable ? "form" : "div"} sx={{ mt: 0, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 0 }}>
          {displayName} Details
        </Typography>
      </Box>

      <Grid container spacing={gridSpacing}>
        {/* Row 1: Name | Email */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Name"
            value={data.name}
            editable={editable}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
            error={errors.name}
            required
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Email"
            value={data.email}
            editable={editable}
            onChange={(e) => onChange({ ...data, email: e.target.value })}
            type="email"
            error={errors.email}
            isEmail
          />
        </Grid>
      </Grid>
    </Box>
  );
};

AnalystFormFields.propTypes = {
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  displayName: PropTypes.string,
};

export default AnalystFormFields;
